import { Languages } from 'lucide-react'
import { useLocale } from '@/i18n/LocaleProvider'
import type { AppLocale } from '@/i18n/types'
import { BRAND } from '@/constants/brand'
import { cn } from '@/utils/cn'

type Props = {
  variant?: 'card' | 'inline'
  tone?: 'onLight' | 'onDark'
  showLabel?: boolean
  className?: string
}

const ON_DARK = {
  border: 'rgba(255,255,255,0.28)',
  surface: 'rgba(255,255,255,0.12)',
  track: 'rgba(255,255,255,0.08)',
  text: '#FFFFFF',
  muted: 'rgba(255,255,255,0.75)',
}

export function LanguageToggle({
  variant = 'card',
  tone = 'onLight',
  showLabel = true,
  className,
}: Props) {
  const { locale, setLocale, t } = useLocale()

  const colors =
    tone === 'onDark'
      ? ON_DARK
      : {
          border: '#E0E0E0',
          surface: '#FFFFFF',
          track: '#F5F5F5',
          text: '#1a1a2e',
          muted: '#6c757d',
        }

  const selectedBg = tone === 'onDark' ? '#FFFFFF' : BRAND
  const selectedText = tone === 'onDark' ? BRAND : '#fff'

  const set = (next: AppLocale) => setLocale(next)

  const segments = (
    <div className="flex rounded-lg p-0.5" style={{ background: colors.track }}>
      {(['en', 'ar'] as const).map((code) => {
        const selected = locale === code
        const label = code === 'en' ? (t.sidebar?.english ?? 'English') : (t.sidebar?.arabic ?? 'العربية')
        return (
          <button
            key={code}
            type="button"
            onClick={() => set(code)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-bold transition-colors',
              selected ? '' : 'hover:opacity-80'
            )}
            style={{
              background: selected ? selectedBg : 'transparent',
              color: selected ? selectedText : colors.muted,
            }}
            aria-pressed={selected}
          >
            {label}
          </button>
        )
      })}
    </div>
  )

  if (variant === 'inline') {
    return <div className={cn('shrink-0', className)}>{segments}</div>
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5',
        className
      )}
      style={{ borderColor: colors.border, background: colors.surface }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Languages className="h-4 w-4 shrink-0" style={{ color: colors.muted }} />
        {showLabel ? (
          <span className="truncate text-sm font-semibold" style={{ color: colors.text }}>
            {t.App.language}
          </span>
        ) : null}
      </div>
      {segments}
    </div>
  )
}
